import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as WhatsAppIcon } from '../../icons/whatsapp.svg';
import { ReactComponent as MailIcon } from '../../icons/mail-05.svg';
import {
  GuestApiError,
  sendGuestCode,
  TodoListGuestVerificationInfo,
  verifyGuestCode,
} from './guestApi';

const CODE_LENGTH = 6;
const DEFAULT_COOLDOWN_SECONDS = 60;

interface Props {
  token: string;
  info: TodoListGuestVerificationInfo;
  /** The device is stored; the caller reloads the list. */
  onVerified: () => void;
  /** The link died (404) while verifying. */
  onInvalid: (whatsappUrl?: string) => void;
}

type CodeError =
  | { kind: 'invalid'; attemptsLeft?: number }
  | { kind: 'expired' }
  | { kind: 'too_many_attempts' }
  | { kind: 'send_failed' }
  | { kind: 'generic' };

/**
 * "Enter the code we sent you" gate in front of a guest list. Shown when the
 * link is live but this browser has no verified device for it: a code goes to
 * the invited WhatsApp number / email as soon as the screen opens, the guest
 * types it, and a correct code stores a device token for the link.
 */
const GuestVerifyScreen: React.FC<Props> = ({
  token,
  info,
  onVerified,
  onInvalid,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<CodeError | null>(null);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const autoSent = useRef(false);

  useEffect(() => {
    const remaining = () =>
      Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
    setCooldown(remaining());
    if (cooldownUntil <= Date.now()) return;
    const timer = window.setInterval(() => {
      const left = remaining();
      setCooldown(left);
      if (left <= 0) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownUntil]);

  const startCooldown = useCallback(
    (seconds: number) => setCooldownUntil(Date.now() + seconds * 1000),
    [],
  );

  const send = useCallback(async () => {
    setSending(true);
    setError(null);
    try {
      const res = await sendGuestCode(token);
      setSentOnce(true);
      startCooldown(res.retryAfterSeconds ?? DEFAULT_COOLDOWN_SECONDS);
      setCode('');
      inputRef.current?.focus();
    } catch (err) {
      if (err instanceof GuestApiError && err.status === 404) {
        onInvalid(err.whatsappUrl);
        return;
      }
      if (err instanceof GuestApiError && err.status === 429) {
        startCooldown(DEFAULT_COOLDOWN_SECONDS);
      }
      setError({ kind: 'send_failed' });
    } finally {
      setSending(false);
    }
  }, [token, onInvalid, startCooldown]);

  useEffect(() => {
    if (autoSent.current) return;
    autoSent.current = true;
    void send();
  }, [send]);

  const submit = useCallback(
    async (typed: string) => {
      if (typed.length !== CODE_LENGTH || checking) return;
      setChecking(true);
      setError(null);
      try {
        const res = await verifyGuestCode(token, typed);
        if (res.status === 'verified') {
          onVerified();
          return;
        }
        setCode('');
        inputRef.current?.focus();
        if (res.status === 'invalid') {
          setError({ kind: 'invalid', attemptsLeft: res.attemptsLeft });
        } else if (res.status === 'expired') {
          setError({ kind: 'expired' });
        } else {
          setError({ kind: 'too_many_attempts' });
        }
      } catch (err) {
        if (err instanceof GuestApiError && err.status === 404) {
          onInvalid(err.whatsappUrl);
          return;
        }
        setError({ kind: 'generic' });
      } finally {
        setChecking(false);
      }
    },
    [token, checking, onVerified, onInvalid],
  );

  // The caret is invisible, so typing always appends and backspace always
  // removes the last digit, whatever the tap landed on.
  const keepCaretAtEnd = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    event.currentTarget.setSelectionRange(value.length, value.length);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    if (error?.kind === 'invalid' || error?.kind === 'generic') setError(null);
    if (digits.length === CODE_LENGTH) void submit(digits);
  };

  const errorText = (() => {
    if (!error) return null;
    switch (error.kind) {
      case 'invalid':
        return error.attemptsLeft !== undefined && error.attemptsLeft > 0
          ? t('todoGuest.verify.errors.invalidWithAttempts', {
              count: error.attemptsLeft,
            })
          : t('todoGuest.verify.errors.invalid');
      case 'expired':
        return t('todoGuest.verify.errors.expired');
      case 'too_many_attempts':
        return t('todoGuest.verify.errors.tooManyAttempts');
      case 'send_failed':
        return t('todoGuest.verify.errors.sendFailed');
      default:
        return t('todoGuest.verify.errors.generic');
    }
  })();

  const needsNewCode =
    error?.kind === 'expired' || error?.kind === 'too_many_attempts';
  const ChannelIcon = info.channel === 'phone' ? WhatsAppIcon : MailIcon;
  const activeCell = Math.min(code.length, CODE_LENGTH - 1);

  return (
    <section
      className='guest-verify'
      data-testid='guest-verify'
      aria-labelledby='guest-verify-title'
    >
      <span className='guest-verify-channel' aria-hidden='true'>
        <ChannelIcon />
      </span>
      <h1 id='guest-verify-title' className='guest-verify-title'>
        {t(
          info.channel === 'phone'
            ? 'todoGuest.verify.titleWhatsApp'
            : 'todoGuest.verify.titleEmail',
        )}
      </h1>
      <p className='guest-verify-body'>
        <Trans
          i18nKey={
            info.channel === 'phone'
              ? 'todoGuest.verify.bodyWhatsApp'
              : 'todoGuest.verify.bodyEmail'
          }
          values={{
            destination: info.destination,
            inviter: info.inviterName,
            list: info.listName,
          }}
          components={{ strong: <strong /> }}
        />
      </p>

      <form
        className='guest-verify-form'
        onSubmit={(event) => {
          event.preventDefault();
          void submit(code);
        }}
      >
        <label className='guest-verify-label' htmlFor='guest-verify-code'>
          {t('todoGuest.verify.codeLabel')}
        </label>
        {/* One real input (keyboard, paste, OTP autofill) drawn as six cells. */}
        <div
          className={`guest-verify-code${error ? ' has-error' : ''}${checking ? ' is-disabled' : ''}`}
        >
          <div className='guest-verify-cells' aria-hidden='true'>
            {Array.from({ length: CODE_LENGTH }, (_, index) => (
              <span
                key={index}
                className={`guest-verify-cell${focused && index === activeCell ? ' is-active' : ''}${index < code.length ? ' is-filled' : ''}`}
                data-testid='guest-verify-cell'
              >
                {code[index] ?? ''}
              </span>
            ))}
          </div>
          <input
            ref={inputRef}
            id='guest-verify-code'
            className='guest-verify-input'
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            autoComplete='one-time-code'
            autoFocus
            maxLength={CODE_LENGTH}
            value={code}
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onSelect={keepCaretAtEnd}
            disabled={checking}
            aria-invalid={!!error}
            aria-describedby={errorText ? 'guest-verify-error' : undefined}
            data-testid='guest-verify-code'
          />
        </div>
        {errorText && (
          <p
            id='guest-verify-error'
            className='guest-verify-error'
            role='alert'
          >
            {errorText}
          </p>
        )}
        <button
          type='submit'
          className='guest-verify-submit'
          disabled={code.length !== CODE_LENGTH || checking}
          data-testid='guest-verify-submit'
        >
          {checking
            ? t('todoGuest.verify.checking')
            : t('todoGuest.verify.submit')}
        </button>
      </form>

      <p className='guest-verify-resend' role='status'>
        {sending ? (
          t('todoGuest.verify.sending')
        ) : cooldown > 0 ? (
          t('todoGuest.verify.resendIn', { count: cooldown })
        ) : (
          <button
            type='button'
            className={`guest-verify-resend-btn${needsNewCode ? ' is-primary' : ''}`}
            onClick={() => void send()}
            data-testid='guest-verify-resend'
          >
            {sentOnce
              ? t('todoGuest.verify.resend')
              : t('todoGuest.verify.sendCode')}
          </button>
        )}
      </p>
    </section>
  );
};

export default GuestVerifyScreen;
